import { useContext, useEffect, useRef, useState } from 'react';
import MainLayout from '../components/MainLayout';
import PageHeader from '../components/PageHeader';
import { FlowForm, FlowFormLeaveContext, useFlowFormGlobal } from 't-lib-ui/structure';
import { TextButton, FocusBlock, A } from 't-lib-ui/widgets';
import { ExpandableSection } from 't-lib-ui/lines';
import { goTo, RouterContext } from 't-lib-ui/router';
import { nonNull } from 't-lib-ui/helpers';
import { sidebarLeaveHandler } from '../components/LeftSidebarFocus';
import { AppState } from '../context/general';
import { parseOpml } from '../connector/api';
import './PreferencesPage.css';
import './ImportPage.css';

interface ScreenshotProps {
  src: string;
  alt: string;
  onOpen: (src: string, alt: string) => void;
}

const Screenshot: React.FC<ScreenshotProps> = ({ src, alt, onOpen }) => (
  <FocusBlock className="importHelpScreenshotButton" onClick={() => onOpen(src, alt)}>
    <img className="importHelpScreenshot" src={src} alt={alt} />
  </FocusBlock>
);

const ImportPage: React.FC = () => {
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  useFlowFormGlobal();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key == 'Escape' || ev.key == 'Enter' || ev.key == ' ') {
        ev.preventDefault();
        ev.stopPropagation();
        ev.stopImmediatePropagation();
        setLightbox(null);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [lightbox]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
    setError(undefined);
    e.target.value = '';
  };

  const onImport = async () => {
    if (!file || busy) return;
    setError(undefined);
    setBusy(true);
    try {
      const auth = nonNull(appState.state.authData);
      const xml = await file.text();
      const result = await parseOpml(auth, xml);
      if (!result.feeds.length) {
        setError('No feeds found in this OPML file.');
        setBusy(false);
        return;
      }
      sessionStorage.setItem('opmlImport', JSON.stringify(result));
      goTo(routerCtx, '/a/import-opml');
    } catch (e) {
      setError('Import failed: ' + (e as Error).message);
      setBusy(false);
    }
  };

  const openLightbox = (src: string, alt: string) => setLightbox({ src, alt });

  return (
    <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
      <MainLayout
        selectedFeed="import"
        right={({ onExpand }) => (
          <div className="preferencesPage">
            <PageHeader title="Import OPML" onMenu={onExpand} />

            <FlowForm main className="preferencesSection">
              <div className="preferencesSectionContent">
                <h2 className="preferencesSectionTitle">Import an OPML file</h2>
                <div className="preferencesGrid">
                  <div className="preferenceCard">
                    <div className="preferenceCardHeader">
                      <p>
                        Upload an OPML file from another RSS reader to bring your subscriptions into
                        Downread. After you choose a file, we'll show you a preview where you can
                        pick which feeds to follow and decide which should stay private.
                      </p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".opml,.xml,application/xml,text/xml"
                      style={{ display: 'none' }}
                      onChange={onFileChange}
                    />
                    <div className="accountButtonRow" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                      <TextButton
                        label={file ? `Selected: ${file.name}` : 'Choose OPML file…'}
                        type="minor"
                        size="small"
                        enabled={busy ? 'disabled' : 'enabled'}
                        onClick={() => fileInputRef.current?.click()}
                      />
                      <TextButton
                        label="Import"
                        type="major"
                        size="small"
                        enabled={file && !busy ? 'enabled' : 'disabled'}
                        busy={busy}
                        onClick={onImport}
                      />
                    </div>
                    {error && (
                      <p className="importError">{error}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="preferencesSectionContent">
                <h2 className="preferencesSectionTitle">How to export from another reader</h2>
                <p className="importHelpLead">
                  Most readers offer an OPML export of your subscriptions. Open the section for the
                  reader you're migrating from for step-by-step instructions.
                </p>
                <div className="importHelpList">
                  <ExpandableSection label="Feedbin" openByDefault={false}>
                    <div className="importHelpBody">
                      <ol>
                        <li>Open the settings menu in the top-right corner of Feedbin.</li>
                        <li>Choose the <strong>Import &amp; Export</strong> tab.</li>
                        <li>On the <strong>Subscriptions</strong> line, click <strong>Download</strong> to save the OPML file.</li>
                      </ol>
                      <Screenshot
                        src="/opml-export/OPML-export-feedbin.png"
                        alt="Feedbin Import & Export screen with the Subscriptions Download button highlighted"
                        onOpen={openLightbox}
                      />
                    </div>
                  </ExpandableSection>

                  <ExpandableSection label="Feedly" openByDefault={false}>
                    <div className="importHelpBody">
                      <ol>
                        <li>Log in to Feedly.</li>
                        <li>Visit <A href="https://feedly.com/i/opml" target="_blank" rel="noreferrer">feedly.com/i/opml</A>.</li>
                        <li>Click <strong>Download Your Feedly OPML</strong> to save the file.</li>
                      </ol>
                      <Screenshot
                        src="/opml-export/OPML-export-feedly.png"
                        alt="Feedly OPML export page with the Download Your Feedly OPML button highlighted"
                        onOpen={openLightbox}
                      />
                    </div>
                  </ExpandableSection>

                  <ExpandableSection label="Inoreader" openByDefault={false}>
                    <div className="importHelpBody">
                      <ol>
                        <li>Open the account menu and choose <strong>Preferences</strong>.</li>
                        <li>Switch to the <strong>Export and backup</strong> tab.</li>
                        <li>Click <strong>Download OPML file</strong> to save your subscription list.</li>
                      </ol>
                      <Screenshot
                        src="/opml-export/OPML-export-inoreader.png"
                        alt="Inoreader Export and backup tab with the Download OPML file option"
                        onOpen={openLightbox}
                      />
                    </div>
                  </ExpandableSection>

                  <ExpandableSection label="NewsBlur" openByDefault={false}>
                    <div className="importHelpBody">
                      <ol>
                        <li>Open the account menu and pick <strong>Settings</strong>, then the <strong>Preferences</strong> tab.</li>
                        <li>Scroll to the bottom of the Preferences dialog and click <strong>Download OPML</strong>.</li>
                      </ol>
                      <Screenshot
                        src="/opml-export/OPML-export-newsblur1.png"
                        alt="NewsBlur account menu showing the Settings entry and Preferences tab"
                        onOpen={openLightbox}
                      />
                      <Screenshot
                        src="/opml-export/OPML-export-newsblur2.png"
                        alt="NewsBlur Preferences dialog scrolled to the Download OPML button"
                        onOpen={openLightbox}
                      />
                    </div>
                  </ExpandableSection>

                  <ExpandableSection label="Feeder" openByDefault={false}>
                    <div className="importHelpBody">
                      <ol>
                        <li>Open <strong>Library</strong>.</li>
                        <li>Switch to the <strong>Import &amp; Export</strong> tab.</li>
                        <li>Click <strong>Export feeds to OPML</strong> to download the file.</li>
                      </ol>
                      <Screenshot
                        src="/opml-export/OPML-export-feeder.png"
                        alt="Feeder Library with the Import & Export tab and Export feeds to OPML button"
                        onOpen={openLightbox}
                      />
                    </div>
                  </ExpandableSection>
                </div>
              </div>
            </FlowForm>

            {lightbox && (
              <div
                className="importLightbox"
                role="dialog"
                aria-modal="true"
                aria-label={lightbox.alt}
                onClick={() => setLightbox(null)}
              >
                <img src={lightbox.src} alt={lightbox.alt} />
              </div>
            )}
          </div>
        )}
        startWithLeftCollapsed={false}
      />
    </FlowFormLeaveContext.Provider>
  );
};

export default ImportPage;
